import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, RotateCcw } from "lucide-react";
import { api, useApi } from "@/lib/api";
import { streamSSE } from "@/lib/stream";

const KEY = "kredito_ai_session";
const newSid = () => `s${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
const EXAMPLES = ["Chi devo richiamare questa settimana?", "Quali polizze scadono nei prossimi 2 mesi?",
  "Quali nominativi non sento da più di un mese?", "Riassumi le pratiche in istruttoria"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#0F172A] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
      {m.content?.replace(/\*\*/g, "") || <span className="inline-flex gap-1"><span className="h-2 w-2 rounded-full bg-slate-300 animate-pulse" /><span className="h-2 w-2 rounded-full bg-slate-300 animate-pulse [animation-delay:150ms]" /></span>}
    </div>
  </div>
);

export default function Assistant() {
  const [sid, setSid] = useState(() => localStorage.getItem(KEY) || newSid());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(true);
  const end = useRef(null);
  const { data: st } = useApi("/settings");

  useEffect(() => {
    localStorage.setItem(KEY, sid);
    setBusy(true);
    api.get(`/ai/assistant/${sid}`).then((r) => setMsgs(r.data || [])).catch(() => {}).finally(() => setBusy(false));
  }, [sid]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    try {
      await streamSSE("/ai/assistant", { session_id: sid, message: m }, (full) =>
        setMsgs((x) => [...x.slice(0, -1), { role: "assistant", content: full }]));
    } catch (e) {
      toast.error(e.message);
      setMsgs((x) => x.slice(0, -2));
    } finally { setBusy(false); }
  };

  if (st?.ai_enabled === false) return (
    <div className="space-y-4 fade-up" data-testid="assistant-disabled">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Assistente AI</h1>
      <p className="text-slate-500">Le funzioni AI sono disattivate. Puoi riattivarle da Impostazioni → Funzioni di intelligenza artificiale.</p>
    </div>
  );
  return (
    <div className="space-y-6 fade-up" data-testid="assistant-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Assistente AI</h1>
          <p className="text-slate-500 mt-2">Chiedi qualsiasi cosa su clienti, richiami e pratiche: risponde con i dati aggiornati del tuo CRM.</p>
        </div>
        <button data-testid="assistant-new-chat" onClick={() => { setMsgs([]); setSid(newSid()); }}
          className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm hover:border-slate-900 shrink-0">
          <RotateCcw className="h-4 w-4" />Nuova chat
        </button>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl flex flex-col h-[calc(100vh-280px)] min-h-[420px]">
        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="assistant-messages">
          {msgs.length === 0 && (
            <div className="h-full grid place-items-center text-center">
              <div className="space-y-4">
                <Sparkles className="h-10 w-10 text-violet-500 mx-auto" />
                <p className="text-slate-500 text-sm">Prova a chiedere:</p>
                <div className="flex flex-wrap justify-center gap-2 max-w-xl">
                  {EXAMPLES.map((e, i) => (
                    <button key={e} data-testid={`assistant-example-${i}`} disabled={busy} onClick={() => send(e)}
                      className="text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-violet-400 disabled:opacity-50">{e}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {msgs.map((m, i) => <Bubble key={i} m={m} />)}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 bg-white rounded-b-xl flex gap-2">
          <input data-testid="assistant-input" value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
            placeholder="Scrivi una domanda…" className="flex-1 h-11 rounded-full border border-slate-200 px-4 text-sm focus:outline-none focus:border-violet-400 disabled:bg-slate-50" />
          <button data-testid="assistant-send-btn" disabled={busy || !text.trim()} className="h-11 w-11 rounded-full bg-violet-600 text-white grid place-items-center disabled:opacity-40 hover:bg-violet-700">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
